import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getLocation, removeAbsoluteUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import { IconPresentationProps, IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal/Modal";
import { Disabled } from "@insite/mobius/Swatch";
import TextField, { TextFieldProps } from "@insite/mobius/TextField/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const parsedQuery = parseQueryString<{ externalError?: string }>(getLocation(state).search);

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        externalError: parsedQuery.externalError,
        createAccountUrl: createAccountPageLink?.url,
        currentLocation: getLocation(state),
    };
};

interface OwnProps {
    onClose: () => void;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    signIn,
};

export interface SignInFormStyles {
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    errorGridItem?: GridItemProps;
    errorColorForms?: InjectableCss;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    menutriangle?: InjectableCss;
    forgotPasswordLinkcustom?: InjectableCss;
    jStart?: InjectableCss;
    registerButton?: InjectableCss;
    borderColorForms?: InjectableCss;
    checkBoxProps?: IconPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

export const signInFormStyles: SignInFormStyles = {
    icon: {
        css: css`
            padding-top: 3.125rem;
        `,
    },
    signInExistingAccountTitle: {
        variant: "h4",
        css: css`
            text-transform: none;
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.2rem;
            font-weight: 700;
            margin-bottom: 1rem;
            display: block;
            text-align: left;
            line-height: 1rem;
            margin-bottom: 2rem;
        `,
    },
    userNameGridItem: {
        width: 12,
        css: css`
            padding: .5rem 1rem;
            text-transform: Capitalize;
        `,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            padding: .5rem 1rem;
            text-transform: Capitalize;
        `,
    },
    errorGridItem: {
        width: 12,
        css: css`
            text-transform: Capitalize;
            padding: .5rem 1rem;
            color: #c63527;
            font-size: .9375rem;
            font-weight: 600;
        `,
    },
    signInButtonGridItem: {
        width: 6,
        css: css`
            justify-content: center;
        `,
    },
    rememberMeGridItem: {
        width: 6,
        css: css`
            padding: .5rem 1rem;
            text-transform: Capitalize;
        `,
    },
    forgotPasswordGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
            padding: .5rem 1rem;
        `,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 14,
            color: "#002554",
        },
    },
    signInButton: {
        css: css`
            &.show-loader {
                background: #ccc;
                border-color: #ccc;
                color: #fff;
                cursor: not-allowed;
                pointer-event: none;
                &:hover,
                &:focus {
                    background: #ccc;
                    border-color: #ccc;
                    color: #fff;
                    cursor: not-allowed;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
                width: 100%;
                height: 2.5rem;
                background-color: #FFB600;
                border-radius: .125rem;
                color:#002554;
                border: none;
                &:hover {
                    background-color: #FF9400; 
                    border: none; 
        `,
    },
    registerButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
            width: 100%;
            height: 2.5rem;
            background-color: #fff;
            border-radius: .125rem;
            border: 2px solid #002554;
            color: #002554;
            &:hover {
                background-color: #2060ad;
                color: #fff;
                border-color: #2060ad;
            }
            &:focus {
                background-color: #002554;
                color: #fff;
                border-color: #002554;
            }
        `,
    },
    resetPasswordModal: {
        sizeVariant: "small",
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
    menutriangle: {
        css: css`
            width: 0;
            height: 0;
            border: 15px solid transparent;
            border-bottom-color: #fff;
            bottom: 0;
            right: 65%;
            z-index: 99;
            position: absolute;
        `,
    },
    jStart: {
        css: css`
            justify-content: flex-start;
            align-items: center;
        `,
    },
    forgotPasswordLinkcustom: {
        css: css`
            font-weight: 600;
        `,
    },
    borderColorForms: {
        css: css`
            border-color: rgba(32, 96, 173, .5) !important;
        `,
    },
    errorColorForms: {
        css: css`
            border: 1px solid #c63527 !important;
            border-radius: .125rem;
            background-color: rgba(198, 53, 39, .13) !important;
        `,
    },
    checkBoxProps: {
        size: 20,
    },
    centeringWrapper: {
        css: css`
            height: auto;
            width: auto;
            position: fixed;
            z-index: 99999;
            left: 50%;
            top: 41%;
            display: flex;
            align-items: center; 
        `,
    },
    spinner: {
        css: css`
            margin: auto;
            color: #002554;
        `,
    },
};

const StyledForm = getStyledWrapper("form");
const styles = signInFormStyles;

const SignInForm: React.FC<Props> = ({ signIn, accountSettings, externalError, onClose, createAccountUrl ,currentLocation}) => {
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState("");
    const [signInLoader, setSignInLoader] = useState(false);
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isSignInFormOpen, setIsSignInFormOpen] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [errorPreset, setErrorPreset] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");
    const [emailError, setEmailError] = useState<ReactNode>("");
    const history = useHistory();

    const showRememberMe = accountSettings.rememberMe;

    const validateEmailForSignIn = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;

        setEmailError(errorMessage);
        return !errorMessage;
    };

    const handleCreateNewAccount = (event: { preventDefault: () => void }) => {
        event.preventDefault();
        if (!createAccountUrl) {
            return;
        }
        history.push(createAccountUrl);
        onClose();
    };

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        setErrorPreset(false);
        if (!email || !pass) {
            return;
        }

        if (!validateEmailForSignIn(email)) {
            return;
        }

        const queryParams = parseQueryString<{ returnUrl?: string }>(window.location.search);
        let returnUrl = removeAbsoluteUrl(queryParams.returnUrl?.toString());
        if (typeof returnUrl === "undefined")
        {
            returnUrl = removeAbsoluteUrl(currentLocation.pathname + currentLocation.search);
        }

        if (returnUrl.trim() === "" && window.location.pathname !== "/") {
            returnUrl = window.location.pathname;
        }

        setErrorMessage("");

        signIn({
            userName: email,
            password: pass,
            rememberMe,
            returnUrl,
            onError: (error: string, statusCode: number | undefined) => {
                if (statusCode === 422) {
                    setIsSignInFormOpen(false);
                    setIsChangePasswordModalOpen(true);
                    return;
                }
                setErrorMessage("Invalid email/password combination");
                setHasError(true);
                setErrorPreset(true);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const forgotPassword = () => {
        setIsResetPasswordModalOpen(true);
        document.getElementById("triangle")?.toggleAttribute("hidden");
        document.getElementById("form")?.toggleAttribute("hidden");
        document.getElementById("overlay")?.toggleAttribute("hidden");
    };

    const toggleErrorInput = hasError ? { ...styles.errorColorForms } : { ...styles.borderColorForms };

    const passwordErrorMessage = !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : "";

    const showSignInLoader = () => {
        setSignInLoader(true);
    }

    return (
        <>
            {isSignInFormOpen &&
                (<>
                    <StyledWrapper id="triangle" {...styles.menutriangle}></StyledWrapper>
                    <StyledWrapper
                        id="form"
                        style={{
                            background: "white",
                            width: "auto",
                            right: "35%",
                            position: "absolute",
                            zIndex: 99999,
                            top: "100%",
                            padding: "1.5rem",
                            borderRadius: "2px",
                        }}
                    >
                        <StyledWrapper
                            style={{
                                width: "18.75rem",
                                margin: "auto",
                                paddingBottom: "1.25rem",
                            }}
                        >
                            <Typography {...styles.signInExistingAccountTitle}>
                                {translate("Sign In")}
                                <div>
                                    <svg className="mb-3" width="20" height="3" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M0 3V0h20v3z" fill="#C63527" fillRule="nonzero"></path>
                                    </svg>
                                </div>
                            </Typography>
                            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                                <GridContainer {...styles.signInGridContainer}>
                                    <GridItem {...styles.userNameGridItem}>
                                        <TextField
                                            {...toggleErrorInput}
                                            id="email"
                                            type="email"
                                            {...styles.userNameTextField}
                                            label={translate("Email")}
                                            onFocus={() => setErrorMessage("")}
                                            onChange={e => setEmail(e.currentTarget.value)}
                                            onBlur={e => validateEmailForSignIn(e.currentTarget.value)}
                                            value={email}
                                            error={emailError}
                                            data-test-selector="signIn_email"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.passwordGridItem}>
                                        <TextField
                                            {...toggleErrorInput}
                                            id="password"
                                            {...styles.passwordTextField}
                                            label={translate("Password")}
                                            onChange={e => setPassword(e.currentTarget.value)}
                                            value={password}
                                            error={passwordErrorMessage}
                                            iconProps={{
                                                ...styles.icon,
                                                src: showPassword ? EyeOff : Eye,
                                            }}
                                            iconClickableProps={{
                                                onClick: () => {
                                                    setShowPassword(!showPassword);
                                                },
                                                type: "button",
                                            }}
                                            clickableText={password}
                                            autoComplete="current-password"
                                            data-test-selector="signIn_password"
                                            type={showPassword ? "text" : "password"}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.forgotPasswordGridItem} {...styles.jStart}>
                                        <Link
                                            {...styles.forgotPasswordLink}
                                            {...styles.forgotPasswordLinkcustom}
                                            type="button"
                                            onClick={forgotPassword}
                                            data-test-selector="signIn_forgotPassword"
                                        >
                                            {translate("Forgot Password ?")}
                                        </Link>
                                        <Modal
                                            headline={translate("Reset Password")}
                                            {...styles.resetPasswordModal}
                                            isOpen={isResetPasswordModalOpen}
                                            handleClose={() => onResetPasswordClose()}
                                            data-test-selector="resetPasswordModal"
                                        >
                                            <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                                        </Modal>
                                    </GridItem>
                                    <GridItem {...styles.rememberMeGridItem}>
                                        {showRememberMe && (
                                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                                <Checkbox
                                                    uid="rememberMe"
                                                    {...styles.rememberMeCheckbox}
                                                    checked={rememberMe}
                                                    onChange={(e, value) => setRememberMe(value)}
                                                    iconProps={{
                                                        ...styles.checkBoxProps,
                                                    }}
                                                >
                                                    {translate("Remember Me")}
                                                </Checkbox>
                                            </CheckboxGroup>
                                        )}
                                    </GridItem>
                                    <GridItem {...styles.errorGridItem}>
                                        <Typography>{errorMessage}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.signInButtonGridItem}>
                                        <Button className={`${signInLoader && errorPreset === false ? 'show-loader' : ''}`} onClick={showSignInLoader} {...styles.signInButton} type="submit" data-test-selector="signIn_submit">
                                            {translate("Sign In")}
                                        </Button>
                                    </GridItem>
                                    <GridItem {...styles.signInButtonGridItem}>
                                        <Button {...styles.registerButton} onClick={handleCreateNewAccount}>
                                            {translate("Register")}
                                        </Button>
                                    </GridItem>
                                </GridContainer>
                            </StyledForm>
                        </StyledWrapper>
                    </StyledWrapper>
                </>
                )}
            <Modal
                headline={translate("Change Password")}
                {...styles.changePasswordModal}
                isOpen={isChangePasswordModalOpen}
                handleClose={onChangePasswordClose}
            >
                <SignInChangePasswordForm
                    enteredUserName={email}
                    enteredPassword={password}
                    // instructionsText={fields.changePasswordInstructions}
                    onPasswordChanged={onPasswordChanged}
                />
            </Modal>
            <StyledWrapper
                // background overlay here
                id="overlay"
                style={{
                    background: "rgba(0, 37, 84, 0.2)",
                    opacity: 1,
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    position: "fixed",
                    zIndex: 5,
                }}
                onClick={onClose}
            >
            </StyledWrapper>
            {signInLoader && errorPreset === false && !isChangePasswordModalOpen && (<StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>)}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SignInForm);
